import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Membership() {
  const [plan, setPlan] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlan((r.data || [])[0] || null));
  }, []);

  const join = async () => {
    if (!user) {
      toast.error("Please sign in to continue with your application.");
      nav("/login?next=/membership");
      return;
    }
    if (!plan) return;
    try {
      const { data } = await api.post("/checkout/create", { plan_id: plan.plan_id });
      nav(`/checkout/${data.order_id}`);
    } catch {
      toast.error("Could not start checkout. Please try again.");
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pt-20 pb-8 text-center">
        <div className="sv-overline mb-4">SV Talent Agency · Join</div>
        <h1 className="font-serif-hero text-4xl lg:text-7xl text-[#F4E4BC]">Apply for SV Talent<br className="hidden sm:block" /> representation.</h1>
        <p className="mt-6 max-w-2xl mx-auto text-[#CDC7BA]">
          A single one-time application fee unlocks review by our casting team.
          Submit your details and portfolio — verified profiles are considered for casting opportunities across fashion, music, advertising and entertainment.
        </p>
      </section>

      <section className="max-w-[720px] mx-auto px-4 sm:px-8 pb-24">
        <div className="relative rounded-2xl p-10 border-2 border-[#C5A059] bg-gradient-to-b from-[#2A2A2F] to-[#1A1A1F] shadow-[0_40px_100px_rgba(197,160,89,0.08)] text-center" data-testid="plan-card-basic">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#DDB870] to-[#C5A059] text-[#0A0A0B] px-5 py-1.5 rounded-full text-[10px] font-mono-label tracking-[0.32em] shadow-lg">
            APPLICATION FEE
          </div>
          <div className="font-mono-label text-[10px] mt-4 text-[#C5A059] tracking-[0.32em]">{plan?.name || "SV TALENT BASIC"}</div>
          <div className="mt-6 flex items-baseline justify-center gap-2">
            <div className="font-serif-hero text-7xl lg:text-8xl text-[#F4E4BC] leading-none">₹{plan?.price ?? 599}</div>
            <div className="text-sm text-[#8A8478]">one-time</div>
          </div>
          <div className="mt-3 text-xs font-mono-label text-[#8A8478] tracking-[0.32em]">SINGLE APPLICATION FEE · NOT A SUBSCRIPTION</div>

          <ul className="mt-10 space-y-3 max-w-md mx-auto text-left">
            {(plan?.features || [
              "One-time application fee — no recurring charges",
              "Talent application review by SV Talent Agency",
              "Verified talent profile once approved",
              "Consideration for casting opportunities",
              "Upload multiple HD photos & video links",
              "Email support",
            ]).map((f) => (
              <li key={f} className="flex gap-3 text-sm text-[#CDC7BA]">
                <Check size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="plan-choose-basic"
            onClick={join}
            className="mt-10 w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#C5A059] text-[#0A0A0B] font-mono-label text-[12px] tracking-[0.32em] hover:bg-[#D9B672] transition"
          >
            JOIN SV TALENT — ₹{plan?.price ?? 599}
          </button>

          <div className="mt-6 flex items-center justify-center gap-4 text-[10px] font-mono-label text-[#8A8478] tracking-[0.28em]">
            <span className="inline-flex items-center gap-1"><Lock size={11} className="text-[#C5A059]" /> SECURE PAYMENT</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-[#C5A059]" /> UPI / CARDS</span>
          </div>
        </div>

        <p className="mt-10 text-center text-xs text-[#8A8478] max-w-2xl mx-auto leading-relaxed">
          The one-time application fee covers profile review and administrative processing.
          Auditions, projects, selection and employment are not guaranteed and remain at the discretion of the SV Talent Agency casting team.
          By continuing you accept our <Link to="/legal" className="text-[#C5A059]">terms and privacy policy</Link>.
        </p>
      </section>
      <Footer />
    </div>
  );
}
